import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Select, Card, Badge } from "../components/ui";
import { ConfirmModal } from "../components/ui/Modal";

const STATI = {
  100000000: { label: "Pianificato", badge: "badge-info", canRegister: true },
  100000001: {
    label: "In scadenza",
    badge: "badge-warning",
    canRegister: true,
  },
  100000002: { label: "Scaduto", badge: "badge-danger", canRegister: true },
  100000003: { label: "Eseguito", badge: "badge-success", canRegister: false },
  100000004: { label: "Annullato", badge: "badge-gray", canRegister: false },
};

function ScadenzeListPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check permissions
  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isFornitore = user?.ruolo === 100000005;
  const canCreate = isAdmin || isRspp || isFornitore;
  const canDelete = isAdmin;
  // Chiunque può registrare interventi (fornitori solo i propri)
  const canRegisterIntervento = true;
  const [scadenze, setScadenze] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rsaFilter, setRsaFilter] = useState(searchParams.get("rsaId") || "");
  const [statoFilter, setStatoFilter] = useState(
    searchParams.get("stato") || "",
  );
  const [rsaList, setRsaList] = useState([]);

  useEffect(() => {
    // Fetch RSA for filter dropdown (non necessario per Fornitore)
    if (!isFornitore) {
      const fetchRsa = async () => {
        try {
          const result = await apiRequest("/rsa");
          setRsaList(result.value || []);
        } catch (error) {
          console.error("Error fetching RSA:", error);
        }
      };
      fetchRsa();
    }
  }, [apiRequest, isFornitore]);

  useEffect(() => {
    const fetchScadenze = async () => {
      try {
        const params = new URLSearchParams();
        if (rsaFilter) params.append("rsaId", rsaFilter);
        if (statoFilter) params.append("stato", statoFilter);

        const result = await apiRequest(`/scadenze?${params.toString()}`);
        setScadenze(result.value || []);
      } catch (error) {
        console.error("Error fetching scadenze:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchScadenze();
  }, [apiRequest, rsaFilter, statoFilter]);

  const handleFilterChange = (key, value) => {
    if (key === "rsaId") setRsaFilter(value);
    if (key === "stato") setStatoFilter(value);

    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (rsaFilter) params.append("rsaId", rsaFilter);
      if (statoFilter) params.append("stato", statoFilter);
      const result = await apiRequest(`/scadenze?${params.toString()}`);
      setScadenze(result.value || []);
    } catch (error) {
      console.error("Error fetching scadenze:", error);
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    try {
      await apiRequest(`/scadenze/${itemToDelete.id}`, { method: "DELETE" });
      showToast("Scadenza eliminata con successo", "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting scadenza:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Scadenze Programmate
          </h1>
          <p className="text-gray-600">Gestisci le scadenze di manutenzione</p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate("/scadenze/nuova")}>
            + Nuova Scadenza
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 flex-wrap">
          {!isFornitore && (
            <select
              value={rsaFilter}
              onChange={(e) => handleFilterChange("rsaId", e.target.value)}
              className="input w-48"
            >
              <option value="">Tutte le RSA</option>
              {rsaList.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          )}
          <select
            value={statoFilter}
            onChange={(e) => handleFilterChange("stato", e.target.value)}
            className="input w-48"
          >
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {Object.entries(STATI).map(([value, { label, badge }]) => {
          const count = scadenze.filter(
            (s) => s.stato === parseInt(value),
          ).length;
          return (
            <div
              key={value}
              className="card text-center cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() =>
                handleFilterChange("stato", statoFilter === value ? "" : value)
              }
            >
              <div className="text-2xl font-bold">{count}</div>
              <span className={badge}>{label}</span>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="card">
        {scadenze.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>RSA</th>
                <th>Fornitore</th>
                <th>Data Scadenza</th>
                <th>Frequenza</th>
                <th>Stato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {scadenze.map((s) => (
                <tr key={s.id}>
                  <td className="font-medium">{s.nome}</td>
                  <td>{s.rsa?.nome || "-"}</td>
                  <td>{s.fornitore?.nome || "-"}</td>
                  <td>
                    {new Date(s.dataScadenza).toLocaleDateString("it-IT")}
                  </td>
                  <td>{s.frequenzaLabel || "-"}</td>
                  <td>
                    <span className={STATI[s.stato]?.badge || "badge-gray"}>
                      {STATI[s.stato]?.label || "N/D"}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2 items-center">
                      {/* Bottone Registra Intervento - visibile per scadenze non completate */}
                      {STATI[s.stato]?.canRegister && canRegisterIntervento && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() =>
                            navigate(
                              `/interventi/nuovo?scadenzaId=${s.id}&rsaId=${s.rsa?.id || ""}`,
                            )
                          }
                          title="Registra intervento per questa scadenza"
                        >
                          Registra
                        </Button>
                      )}
                      <Link
                        to={`/scadenze/${s.id}`}
                        className="text-primary-600 hover:underline text-sm"
                      >
                        Dettagli
                      </Link>
                      {canDelete && (
                        <button
                          onClick={() => confirmDelete(s)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          title="Elimina"
                        >
                          Elimina
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 text-center py-8">
            Nessuna scadenza trovata
          </p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Scadenza"
        message={`Sei sicuro di voler eliminare la scadenza "${itemToDelete?.nome}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}

export default ScadenzeListPage;
